import React, {
  createRef,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import classnames from 'classnames';

import styles from './FileDragDrop.module.scss';

function FileDragDrop({
  children,
  className,
  draggingContentClassName,
  onDrop,
}) {
  const fileDropper = createRef();
  /*
   * Drag counter is used for counting how many dragIn and dragOut
   * events have occurred. Child components might trigger multiple dragIn
   * and dragOut events so its important to keep track of exactly how many such
   * events have occurred. Only when the counter is 0, we can say that dragging
   * has stopped
   */
  const dragCounter = useRef(0);
  const [isDragging, setDragging] = useState(false);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragIn = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current += 1;
    const { items } = event.dataTransfer;
    if (items && items.length > 0) {
      setDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounter.current -= 1;
    if (dragCounter.current > 0) return;
    setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      setDragging(false);
      const { files } = event.dataTransfer;
      if (files && files.length > 0) {
        onDrop(files);
        event.dataTransfer.clearData();
        dragCounter.current = 0;
      }
    },
    [onDrop]
  );

  useEffect(() => {
    const fileDropDiv = fileDropper.current;
    fileDropDiv.addEventListener('dragenter', handleDragIn);
    fileDropDiv.addEventListener('dragleave', handleDragOut);
    fileDropDiv.addEventListener('dragover', handleDragOver);
    fileDropDiv.addEventListener('drop', handleDrop);

    return () => {
      fileDropDiv.removeEventListener('dragenter', handleDragIn);
      fileDropDiv.removeEventListener('dragleave', handleDragOut);
      fileDropDiv.removeEventListener('dragover', handleDragOver);
      fileDropDiv.removeEventListener('drop', handleDrop);
    };
  }, [fileDropper, handleDragIn, handleDragOut, handleDragOver, handleDrop]);

  return (
    <div
      className={classnames(styles.dragDropContainer, className)}
      ref={fileDropper}
    >
      {isDragging && (
        <div className={styles.draggingContainer}>
          <div
            className={classnames(
              styles.draggingContent,
              draggingContentClassName
            )}
          >
            Drop your files here
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export default FileDragDrop;
